import { useEffect, useState, type FormEvent } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { clips } from "@/mocks/clips";

const navItems = [
  { to: "/", label: "首页", end: true },
  { to: "/clips", label: "爆料片段库", end: false },
  { to: "/clips?sort=hot", label: "高能榜单", end: false },
  { to: "/about", label: "关于我们", end: false },
];

export default function Navbar() {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("bolao-theme");
    const prefersDark = saved === "dark";
    setDark(prefersDark);
    document.documentElement.classList.toggle("dark", prefersDark);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("bolao-theme", next ? "dark" : "light");
  };

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    setMenuOpen(false);
    navigate(q ? `/clips?q=${encodeURIComponent(q)}` : "/clips");
  };

  const tickers = clips.slice(0, 6);
  const tickerList = [...tickers, ...tickers];

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="hidden h-9 items-center overflow-hidden bg-secondary-900 text-secondary-50 md:flex">
        <div className="mx-auto flex w-full max-w-content items-center gap-4 px-4 md:px-6">
          <span className="inline-flex shrink-0 items-center gap-1.5 text-xs font-label text-accent-300">
            <span className="bolao-live-dot inline-block h-1.5 w-1.5 rounded-full bg-primary-400" />
            实时爆料更新中
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="bolao-marquee flex w-max items-center gap-8 whitespace-nowrap">
              {tickerList.map((item, index) => (
                <span key={`${item.id}-${index}`} className="text-xs font-label text-secondary-100/90">
                  {item.title}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`w-full border-b transition-colors ${
          scrolled
            ? "border-background-200 bg-background-50/95 backdrop-blur"
            : "border-transparent bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 w-full max-w-content items-center justify-between gap-4 px-4 md:px-6">
          <NavLink to="/" className="flex items-center gap-2.5" aria-label="爆料现场首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-live-line text-lg" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-bold text-foreground-950">爆料现场</span>
              <span className="mt-1 text-[10px] font-label tracking-[0.18em] text-foreground-500 uppercase">
                Live Leak Archive
              </span>
            </span>
          </NavLink>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-md px-3 py-2 text-sm font-label transition-colors ${
                    isActive && item.to.indexOf("sort=hot") === -1
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-200 hover:text-foreground-950"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="hidden items-center md:flex" role="search">
              <label className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 focus-within:border-primary-400">
                <i className="ri-search-line text-sm text-foreground-500" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索爆料片段 / 主播"
                  className="w-36 bg-transparent text-sm font-label text-foreground-900 outline-none placeholder:text-foreground-500 lg:w-44"
                />
              </label>
            </form>

            <button
              type="button"
              onClick={toggleTheme}
              aria-label={dark ? "切换到浅色模式" : "切换到深色模式"}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-200"
            >
              <i className={dark ? "ri-sun-line" : "ri-moon-line"} />
            </button>

            <button
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="打开导航菜单"
              aria-expanded={menuOpen}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-200 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-background-200 bg-background-50 lg:hidden">
            <div className="mx-auto flex w-full max-w-content flex-col gap-4 px-4 py-4 md:px-6">
              <form onSubmit={handleSearch} role="search" className="flex items-center gap-2">
                <label className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2">
                  <i className="ri-search-line text-sm text-foreground-500" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="搜索爆料片段 / 主播"
                    className="w-full bg-transparent text-sm font-label text-foreground-900 outline-none placeholder:text-foreground-500"
                  />
                </label>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-label text-background-50"
                >
                  搜索
                </button>
              </form>
              <nav className="flex flex-col" aria-label="移动端导航">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `rounded-md px-3 py-2.5 text-sm font-label transition-colors ${
                        isActive && item.to.indexOf("sort=hot") === -1
                          ? "bg-primary-100 text-primary-700"
                          : "text-foreground-700 hover:bg-background-200"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}